import React, { FunctionComponent, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton, ListItem, ListItemButton, ListItemText } from '@mui/material';

import { CalculatedLine, Member } from '@paragon/contract-library';
import { OptionalTooltip } from '@paragon/material';
import { enqueueInfo } from '@paragon/notification-tools';

import { removeMemberCutReferenceLine } from '../../../../store/actions/insertActions';
import { getActiveComponentDesign } from '../../../../store/selectors/getActiveComponentDesignSelector';
import { getReferenceLineLongNamesByGuidForActiveComponent } from '../../../../store/selectors/getReferenceLineLongNamesByGuidForActiveComponentSelector';
import { getPickingObjectInfo } from '../../../../store/selectors/pickingSelectors';
import { applicationOnPickingMemberCutLineGuid, clearPickingObject } from '../../../../store/slices/applicationSlice';
import {
  clearHighlightedReferenceLineGuids,
  setHighlightedReferenceLineGuids,
} from '../../../../store/slices/viewportSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';

type Props = {
  member: Member;
  isForBaseCut: boolean;
  cutEndTerm: string;
  index: number;

  disabled: boolean;

  // These properties are not set if it is for a new cut
  cutGuid?: string;
  cutLine?: CalculatedLine;
};

export const CutRow: FunctionComponent<Props> = (props) => {
  const { cutGuid, cutLine, disabled, index, isForBaseCut, cutEndTerm, member } = props;
  const dispatch = useAppDispatch();

  const memberCutTargetInfo = useAppSelector((state) => {
    const info = getPickingObjectInfo(state);
    return info && info.type === 'pickingReferenceLine' && info.targetInfo.type === 'memberCut'
      ? info.targetInfo
      : null;
  });
  const pickingLine = useAppSelector((state) => getPickingObjectInfo(state)?.type === 'pickingReferenceLine');
  const componentDesign = useAppSelector(getActiveComponentDesign)!;
  const referenceLineLongNamesByGuid = useAppSelector(getReferenceLineLongNamesByGuidForActiveComponent);

  const getCutText = (cutGuid: string, cutLineGuid: string) =>
    memberCutTargetInfo?.replacingLineGuid === cutLineGuid
      ? 'Selecting Line'
      : (referenceLineLongNamesByGuid?.get(cutGuid) ?? '');
  const pickingForThisCut =
    !!cutLine && !!memberCutTargetInfo && memberCutTargetInfo.replacingLineGuid === cutLine.guid;
  const isForExistingCut = !!cutGuid && !!cutLine;

  const canEdit = !disabled && !pickingLine;
  const canDelete = !(disabled || isForExistingCut
    ? (pickingLine && !pickingForThisCut) ||
      member.relativeGeometry![isForBaseCut ? 'baseCutGuids' : 'endCutGuids'].length < 2
    : false);

  const [isHovered, setIsHovered] = useState(false);

  return (
    <ListItem
      key={index}
      secondaryAction={
        <OptionalTooltip disableInteractive title="Delete Cut" visible={canDelete}>
          <IconButton
            disabled={!canDelete}
            id={`${isForExistingCut ? cutGuid : 'new'}-delete-cut-button`}
            edge="end"
            onClick={() => {
              if (isForExistingCut) {
                dispatch(removeMemberCutReferenceLine(member, componentDesign.guid, isForBaseCut, index));
              }
              dispatch(clearPickingObject());
            }}
          >
            <DeleteIcon />
          </IconButton>
        </OptionalTooltip>
      }
      disablePadding
    >
      <ListItemButton
        disabled={!canEdit}
        onMouseEnter={() => {
          setIsHovered(true);
          if (cutLine?.guid) dispatch(setHighlightedReferenceLineGuids(cutLine.guid));
        }}
        onMouseLeave={() => {
          setIsHovered(false);
          dispatch(clearHighlightedReferenceLineGuids());
        }}
        onClick={() => {
          dispatch(
            enqueueInfo(
              'You can create a new reference line using the "Add Item" button in the toolbar if the line you need doesn\'t exist',
            ),
          );
          dispatch(
            applicationOnPickingMemberCutLineGuid({
              replacingLineGuid: cutLine?.guid,
              relativeGeometryGuid: member.relativeGeometry!.guid,
              isForBaseCut,
            }),
          );
        }}
      >
        <ListItemText
          primary={isForExistingCut ? getCutText(cutGuid!, cutLine!.guid) : 'Selecting Line'}
          secondary={isHovered ? 'Click to edit cut' : `${cutEndTerm} Cut #${index + 1}`}
        />
      </ListItemButton>
    </ListItem>
  );
};
